import { DAILY_WORK_HOURS } from "../config/constants";

// 內部：將 File 壓縮成指定 maxWidth + quality 的 base64 data URL
const compressToDataUrl = (file, maxWidth, quality) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scaleSize = maxWidth / img.width;
        const width = scaleSize < 1 ? maxWidth : img.width;
        const height = scaleSize < 1 ? img.height * scaleSize : img.height;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error("圖片載入失敗"));
    };
    reader.onerror = reject;
  });

// 上傳圖片：以 Base64 內嵌儲存（Firestore），多段壓縮確保 < 500KB
// 回傳 { url, isBase64: true, stage }
export const uploadProofImage = async (file /*, uid */) => {
  if (!file) throw new Error("無檔案");
  // 1. 原檔大小防呆 (2MB)
  if (file.size > 2 * 1024 * 1024) {
    throw new Error("圖片過大，請小於 2MB");
  }
  // 2. 副檔名/MIME 驗證
  if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)) {
    throw new Error("僅支援 JPG / PNG / WebP 圖片");
  }

  // 3. 多段壓縮，確保 base64 字串 < 500KB (Firestore 單檔 1MiB 上限的安全範圍)
  const MAX_BASE64_BYTES = 500 * 1024;
  const stages = [
    { maxWidth: 800, quality: 0.6 },
    { maxWidth: 700, quality: 0.5 },
    { maxWidth: 600, quality: 0.45 },
    { maxWidth: 500, quality: 0.4 },
    { maxWidth: 400, quality: 0.35 },
  ];

  let lastUrl = null;
  for (let i = 0; i < stages.length; i++) {
    const { maxWidth, quality } = stages[i];
    const url = await compressToDataUrl(file, maxWidth, quality);
    lastUrl = url;
    if (url.length <= MAX_BASE64_BYTES) {
      return {
        url,
        isBase64: true,
        stage: i + 1,
        sizeKB: Math.round(url.length / 1024),
      };
    }
  }

  throw new Error(
    `圖片即使極限壓縮後仍超過 500KB（${Math.round(
      lastUrl.length / 1024
    )}KB），請改拍簡單一點或裁切後再上傳。`
  );
};

// [向下相容] 舊呼叫端可能仍在用 compressImage()，保留函式但已不在表單使用
export const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 800;
        const scaleSize = maxWidth / img.width;
        const width = scaleSize < 1 ? maxWidth : img.width;
        const height = scaleSize < 1 ? img.height * scaleSize : img.height;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };
    };
    reader.onerror = (error) => reject(error);
  });
};

// 工時計算 (防呆增強版)
export const calculateLeaveStats = (startStr, endStr) => {
  // 1. 基礎檢查
  if (!startStr || !endStr) return { days: 0, hours: 0 };

  const start = new Date(startStr);
  const end = new Date(endStr);

  // 2. [關鍵修復] 檢查是否為有效日期 (避免輸入途中當機)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { days: 0, hours: 0 };
  }

  // 3. 邏輯檢查
  if (start >= end) return { days: 0, hours: 0 };

  let totalMinutes = 0;
  let current = new Date(start);

  // 防止無窮迴圈的保險機制 (若日期差異過大)
  const SAFETY_BREAK = 100000;
  let loopCount = 0;

  while (current < end) {
    if (loopCount++ > SAFETY_BREAK) break;

    const nextMinute = new Date(current.getTime() + 60000);
    if (nextMinute > end) break;
    const hour = current.getHours();
    const minute = current.getMinutes();
    const timeVal = hour + minute / 60;

    // 09:00~18:00 且 排除 12:00~13:30
    const isWorkingHours = timeVal >= 9 && timeVal < 18;
    const isLunchBreak = timeVal >= 12 && timeVal < 13.5;

    if (isWorkingHours && !isLunchBreak) totalMinutes++;
    current = nextMinute;
  }

  let hours = parseFloat((totalMinutes / 60).toFixed(1));
  const days = parseFloat((hours / DAILY_WORK_HOURS).toFixed(2));
  return { days, hours };
};

// 格式化顯示
export const formatDurationDisplay = (days, hours) => {
  if (days >= 1) return `${days} 天`;
  return `${hours} 小時`;
};

// 取得預設下一個工作日時間
export const getNextWorkdayDefaults = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  if (d.getDay() === 6) d.setDate(d.getDate() + 2);
  else if (d.getDay() === 0) d.setDate(d.getDate() + 1);

  const start = new Date(d);
  start.setHours(9, 0, 0, 0);
  const end = new Date(d);
  end.setHours(18, 0, 0, 0);

  const toISO = (date) => {
    const pad = (n) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };
  return { start: toISO(start), end: toISO(end) };
};

// 產生時間選項 (08:00 - 20:00)
export const generateTimeOptions = () => {
  const options = [];
  for (let h = 8; h <= 20; h++) {
    const hourStr = h.toString().padStart(2, "0");
    options.push(`${hourStr}:00`);
    if (h !== 20) options.push(`${hourStr}:30`);
  }
  return options;
};

// 拆解 ISO 時間字串 (安全版)
export const splitDateTime = (isoStr) => {
  if (!isoStr || typeof isoStr !== "string") return ["", "09:00"];
  if (!isoStr.includes("T")) return [isoStr, "09:00"];
  const parts = isoStr.split("T");
  return [parts[0] || "", parts[1] ? parts[1].slice(0, 5) : "09:00"];
};

// 取得假別對應顏色
export const getLeaveTypeColor = (typeId) => {
  switch (typeId) {
    case "personal":
      return "bg-indigo-500"; // 事假：靛藍紫
    case "sick":
      return "bg-rose-400"; // 病假：柔和玫瑰
    case "annual":
      return "bg-emerald-500"; // 特休：鮮綠
    case "official":
      return "bg-amber-400"; // 公假：琥珀色
    case "funeral":
      return "bg-slate-600"; // 喪假：深灰色
    case "marriage":
      return "bg-pink-400"; // 婚假：柔粉色
    case "menstrual":
      return "bg-violet-400"; // 生理假：紫色
    default:
      return "bg-sky-500"; // 其他：清透藍
  }
};

export const getUserColor = (name) => {
  if (!name) return "bg-slate-500";
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-teal-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};
